import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
    Button, Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea
} from "@/components/ui";
import type { CompanyProfile } from "@/types/company";

interface CompanyFormProps {
    company?: Partial<CompanyProfile>;
    onSubmit: (data: Partial<CompanyProfile>) => void;
    onCancel: () => void;
    mode?: "view" | "edit";
}

export const CompanyForm = ({ company, onSubmit, onCancel, mode = "edit" }: CompanyFormProps) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState<Partial<CompanyProfile>>(company || {});

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSubmit(formData);
    };

    const isViewMode = mode === "view";

    return (
        <Dialog open onOpenChange={onCancel}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>
                        {isViewMode
                            ? t("company.viewTitle")
                            : company?.companyName === ""
                                ? t("company.addTitle")
                                : t("company.editTitle")}
                    </DialogTitle>
                    <DialogDescription>
                        {isViewMode
                            ? t("company.viewDescription")
                            : company?.companyName === ""
                                ? t("company.addDescription")
                                : t("company.editDescription")}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="companyName">{t("company.profile.companyName")}</Label>
                                <Input
                                    id="companyName"
                                    value={formData.companyName || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, companyName: e.target.value })
                                    }
                                    disabled={isViewMode}
                                    required
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="psNumber">{t("company.profile.psNumber")}</Label>
                                <Input
                                    id="psNumber"
                                    value={formData.psNumber || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, psNumber: e.target.value })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="glnNumber">{t("company.profile.glnNumber")}</Label>
                                <Input
                                    id="glnNumber"
                                    value={formData.glnNumber || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, glnNumber: e.target.value })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="generalEmail">{t("company.generalEmail")}</Label>
                                <Input
                                    id="generalEmail"
                                    type="email"
                                    value={formData.generalEmail || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, generalEmail: e.target.value })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="productEmail">{t("company.productEmail")}</Label>
                                <Input
                                    id="productEmail"
                                    type="email"
                                    value={formData.productEmail || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, productEmail: e.target.value })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="website">{t("company.website")}</Label>
                                <Input
                                    id="website"
                                    value={formData.website || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, website: e.target.value })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="phone">{t("company.phone")}</Label>
                                <Input
                                    id="phone"
                                    value={formData.phone || ""}
                                    onChange={(e) =>
                                        setFormData({ ...formData, phone: e.target.value })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="subscriptionType">{t("company.subscriptionType")}</Label>
                                <Select
                                    value={formData.subscription?.type || "basic"}
                                    onValueChange={(value: "basic" | "premium" | "premium+") =>
                                        setFormData({
                                            ...formData,
                                            subscription: {
                                                ...formData.subscription,
                                                type: value,
                                                startDate: formData.subscription?.startDate || new Date().toISOString().split('T')[0],
                                                extraServices: formData.subscription?.extraServices || ""
                                            }
                                        })
                                    }
                                    disabled={isViewMode}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="basic">{t("company.subscriptions.basic")}</SelectItem>
                                        <SelectItem value="premium">{t("company.subscriptions.premium")}</SelectItem>
                                        <SelectItem value="premium+">{t("company.subscriptions.premiumPlus")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h3 className="text-lg font-medium">{t("company.profile.address")}</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="street">{t("company.profile.street")}</Label>
                                <Input
                                    id="street"
                                    value={formData.address?.street || ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            address: {
                                                street: e.target.value,
                                                postalCode: formData.address?.postalCode || "",
                                                city: formData.address?.city || "",
                                                country: formData.address?.country || ""
                                            }
                                        })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="postalCode">{t("company.profile.postalCode")}</Label>
                                <Input
                                    id="postalCode"
                                    value={formData.address?.postalCode || ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            address: {
                                                street: formData.address?.street || "",
                                                postalCode: e.target.value,
                                                city: formData.address?.city || "",
                                                country: formData.address?.country || ""
                                            }
                                        })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="city">{t("company.profile.city")}</Label>
                                <Input
                                    id="city"
                                    value={formData.address?.city || ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            address: {
                                                street: formData.address?.street || "",
                                                postalCode: formData.address?.postalCode || "",
                                                city: e.target.value,
                                                country: formData.address?.country || ""
                                            }
                                        })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="country">{t("company.profile.country")}</Label>
                                <Input
                                    id="country"
                                    value={formData.address?.country || ""}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            address: {
                                                street: formData.address?.street || "",
                                                postalCode: formData.address?.postalCode || "",
                                                city: formData.address?.city || "",
                                                country: e.target.value
                                            }
                                        })
                                    }
                                    disabled={isViewMode}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">{t("company.profile.companyDescription")}</Label>
                        <Textarea
                            id="description"
                            value={formData.description || ""}
                            onChange={(e) =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            disabled={isViewMode}
                        />
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onCancel}>
                            {isViewMode ? t("common.close") : t("common.cancel")}
                        </Button>
                        {!isViewMode && (
                            <Button type="submit">
                                {company?.companyName === undefined ? t("common.add") : t("common.save")}
                            </Button>
                        )}
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
